// src/components/modals/CreatureModal.jsx
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * CreatureModal
 * Props:
 * - creature: { name, blogPost, versionNumber?, releaseDate?, description? }
 * - theme: 'light' | 'dark'
 * - onClose: () => void
 */
export default function CreatureModal({ creature, theme = 'light', onClose }) {
  const isOpen = !!creature;

  // refs/hooks must be declared unconditionally
  const closeRef = useRef(null);

  // Close on ESC + lock body scroll (only when open)
  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);

    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose]);

  // Autofocus the close button (only when open)
  useEffect(() => {
    if (!isOpen) return;
    closeRef.current?.focus();
  }, [isOpen]);

  if (!isOpen) return null;

  const isLight = theme === 'light';
  const bgClass = isLight ? 'bg-white' : 'bg-gray-800';
  const textClass = isLight ? 'text-gray-900' : 'text-gray-100';
  const closeClass = isLight ? 'text-gray-600 hover:text-black' : 'text-gray-300 hover:text-white';

  const overlay = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      style={{ zIndex: 10000 }}   // ensure above fish layers
      onClick={onClose}
      aria-modal="true"
      role="dialog"
    >
      <div
        className={`${bgClass} p-6 rounded-lg max-w-sm w-full relative shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={closeRef}
          className={`absolute top-2 right-2 ${closeClass} focus:outline-none`}
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>

        <h3 className={`text-xl font-bold mb-4 ${textClass}`}>{creature.name}</h3>
        {creature.versionNumber && (
          <p className={`mb-2 font-bold ${textClass}`}>Java {creature.versionNumber}</p>
        )}
        {creature.releaseDate && (
          <p className={`mb-2 ${textClass}`}>{creature.releaseDate}</p>
        )}
        {creature.description && (
          <div className={`mb-4 ${textClass}`}>{creature.description}</div>
        )}
        {creature.blogPost && (
          <a
            href={creature.blogPost}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Read Blog Post
          </a>
        )}
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
